import { useMemo } from "react";
import { CurrentUserAuthorizationDTO } from "../../models/auth/current-user-authorization";

import { useAppSelector } from "../redux/redux";
import { ACCESS_TYPES, AUTHORIZATIONS } from "../../utils/constants/authorization-constants";
import { CurrentUserDataDTO } from "../../models/user/current-user-data-dto";
import { WIDGET_CATEGORIES } from "src/utils/constants/constants";

export const isAuthorized = (
  requiredAccessDomains: string[],
  currentUserAuthorizations: CurrentUserAuthorizationDTO | null
): boolean => {
  let result = false || (currentUserAuthorizations?.canViewAll ?? false);
  if (!!requiredAccessDomains?.length && !!currentUserAuthorizations?.allowedDomains.length) {
    const rds = requiredAccessDomains.filter((d) => d !== null && d !== undefined);
    const requiredDomains = rds.map((rd) => rd.toLowerCase());
    requiredDomains.forEach((accessDomain, index) => {
      result = result || currentUserAuthorizations.allowedDomains.includes(accessDomain);
    });
  }

  return result;
};

export const isAuthorizedLE = (
  requiredAccessDomains: string[],
  currentUserAuthorizations: CurrentUserAuthorizationDTO | null,
  leIds: string[] | null = null,
  strictlyUseAccesDomains: boolean = false // This is in case even if the user has view all we strictly consider the requiredAccessDomains
): boolean => {
  let result = false || (!strictlyUseAccesDomains && (currentUserAuthorizations?.canViewAll ?? false));
  if (!!requiredAccessDomains?.length && !!currentUserAuthorizations?.allowedDomains.length) {
    requiredAccessDomains = requiredAccessDomains.map((ad) => ad?.toLowerCase());

    requiredAccessDomains.forEach((accessDomain, index) => {
      const foundForLE = currentUserAuthorizations?.allowedDomainsPerLE?.filter((lea) => {
        return leIds?.includes(lea.key) && !!lea.value?.find((auth) => requiredAccessDomains.includes(auth));
      });
      if (!!foundForLE) {
        result = (result || foundForLE[0]?.value?.includes(accessDomain)) ?? false;
      }
    });
  }

  return result;
};

export const isAuthorizedEeGroup = (
  requiredAccessDomains: string[],
  currentUserAuthorizations: CurrentUserAuthorizationDTO | null,
  eeGroupIds: string[] | null = null,
  strictlyUseAccesDomains: boolean = false // This is in case even if the user has view all we strictly consider the requiredAccessDomains
): boolean => {
  let result = false || (!strictlyUseAccesDomains && (currentUserAuthorizations?.canViewAll ?? false));
  if (!!requiredAccessDomains?.length && !!currentUserAuthorizations?.allowedDomains.length) {
    requiredAccessDomains = requiredAccessDomains.map((ad) => ad?.toLowerCase());

    requiredAccessDomains.forEach((accessDomain, index) => {
      const foundForEeGr = currentUserAuthorizations?.allowedDomainsPerEeGroup?.filter(
        (eega) => eeGroupIds?.includes(eega.key) && !!eega.value?.find((auth) => requiredAccessDomains.includes(auth))
      );
      if (!!foundForEeGr) {
        result = result || (foundForEeGr[0]?.value?.includes(accessDomain) ?? false);
      }
    });
  }

  return result;
};

export const isLinkAuthorized = (
  requiredAccessDomains: string[],
  currentUserAuthorizations: CurrentUserAuthorizationDTO | null
): boolean => {
  let result = false || (currentUserAuthorizations?.canViewAll ?? false);

  if (!!requiredAccessDomains?.length && !!currentUserAuthorizations?.allowedDomains.length) {
    requiredAccessDomains = requiredAccessDomains.map((ad) => ad?.toLowerCase());

    requiredAccessDomains.forEach((accessDomain, index) => {
      result = result || currentUserAuthorizations.allowedDomains.includes(accessDomain);
    });
  }

  return result;
};

export const isUrlAuthorized = (
  requiredAccessDomains: string[],
  links: WidgetLinksProp[] | undefined,
  currentUserAuthorizations: CurrentUserAuthorizationDTO | null,
  currentUserData: CurrentUserDataDTO | null
): boolean => {
  const hasAccess = currentUserAuthorizations?.canViewAll ?? false;

  const systemAccessIds = Object.values(AUTHORIZATIONS.SystemAccess);

  if (hasAccess && !requiredAccessDomains.some((el) => systemAccessIds.includes(el))) {
    return true;
  }

  if (!currentUserAuthorizations || !currentUserAuthorizations.allowedDomains) {
    return false;
  }

  if (requiredAccessDomains.length === 0) {
    return true;
  }

  requiredAccessDomains = requiredAccessDomains.map((ad) => ad?.toLowerCase());

  let result = false;

  requiredAccessDomains.forEach((accessDomain, index) => {
    const found = currentUserAuthorizations.allowedDomains.includes(accessDomain);
    result = result || found;
  });

  const widgetData = currentUserData?.allowedWidgets.find(
    (el) =>
      el.linkAuthorizations &&
      el.linkAuthorizations?.find((el) => (links ? el?.id?.toLowerCase() === links[0]?.id.toLowerCase() : false))
  );

  const isLinkAllowed = links?.find((link) => {
    const linkAuthRules = widgetData?.linkAuthorizations?.find(
      (el) => el?.id?.toLowerCase() === link?.id.toLowerCase()
    );
    return (
      isLinkAuthorized(linkAuthRules?.authorizations ?? [], currentUserAuthorizations) ||
      hasAccess ||
      widgetData?.category?.id === WIDGET_CATEGORIES.MYSELF
    );
  });

  result = result || !!isLinkAllowed;

  return result;
};

export const isTabAuthorized = (
  link: WidgetLinksProp | undefined,
  currentUserAuthorizations: CurrentUserAuthorizationDTO | null,
  currentUserData: CurrentUserDataDTO | null
): boolean => {
  const hasAccess = currentUserAuthorizations?.canViewAll ?? false;

  if (hasAccess) {
    return true;
  }

  if (!currentUserAuthorizations || !currentUserAuthorizations.allowedDomains) {
    return false;
  }

  const widgetData = currentUserData?.allowedWidgets.find(
    (el) =>
      el.linkAuthorizations &&
      el.linkAuthorizations?.find((el) => (link ? el?.id?.toLowerCase() === link?.id.toLowerCase() : false))
  );

  const linkAuthRules = widgetData?.linkAuthorizations?.find((el) => el?.id?.toLowerCase() === link?.id.toLowerCase());

  return isLinkAuthorized(linkAuthRules?.authorizations ?? [], currentUserAuthorizations);
};

export const attemptingSystemConfigAccess = (
  requiredAccessDomains: string[],
  currentUserAuthorizations: CurrentUserAuthorizationDTO | null
): boolean => {
  let result = false;
  if (!!requiredAccessDomains?.length && !!currentUserAuthorizations?.allowedDomains.length) {
    requiredAccessDomains = requiredAccessDomains.map((ad) => ad?.toLowerCase());

    const systemConfigDomains = [
      AUTHORIZATIONS.SystemAccess.ManageUserAdminAccess,
      AUTHORIZATIONS.SystemAccess.ServiceProvidersConfig,
      AUTHORIZATIONS.SystemAccess.SystemConfig,
    ].map((scd) => scd?.toLowerCase());

    let attemptsSystemConfigAccess = false;

    requiredAccessDomains.forEach((accessDomain) => {
      const attempted = systemConfigDomains.includes(accessDomain);
      attemptsSystemConfigAccess = attempted || attemptsSystemConfigAccess;
    });
    result = result || attemptsSystemConfigAccess;
  }

  return result;
};

export const canViewOrChangeAllClientData = (
  currentUserAuthorizations: CurrentUserAuthorizationDTO | null
): boolean => {
  return currentUserAuthorizations?.canViewAll ?? false;
};

const useAuthorization = (
  requiredAccessDomains: string[],
  leIds: string[] | null = null,
  eeGroupIds: string[] | null = null
): boolean => {
  const { currentUserAuthorizations } = useAppSelector((state) => state.currentUserReducer);

  const isAuthorizedResult = useMemo(
    () => isAuthorized(requiredAccessDomains, currentUserAuthorizations),
    [requiredAccessDomains, currentUserAuthorizations]
  );

  const isAuthorizedLeResult = useMemo(
    () => isAuthorizedLE(requiredAccessDomains, currentUserAuthorizations, leIds),
    [requiredAccessDomains, currentUserAuthorizations, leIds]
  );

  const isAuthorizedEeGroupResult = useMemo(
    () => isAuthorizedEeGroup(requiredAccessDomains, currentUserAuthorizations, eeGroupIds),
    [requiredAccessDomains, currentUserAuthorizations, eeGroupIds]
  );

  const canViewOrChangeClientDataResult = useMemo(
    () => currentUserAuthorizations?.canViewAll ?? false,
    [currentUserAuthorizations]
  );

  const attemptingSystemConfigAccessResult = useMemo(
    () => attemptingSystemConfigAccess(requiredAccessDomains, currentUserAuthorizations),
    [requiredAccessDomains, currentUserAuthorizations]
  );

  let result = false;

  if (!!currentUserAuthorizations) {
    result =
      isAuthorizedResult ||
      isAuthorizedLeResult ||
      isAuthorizedEeGroupResult ||
      (!attemptingSystemConfigAccessResult && canViewOrChangeClientDataResult);
  }

  return result;
};

export const useLeAuthorization = (requiredAccessDomains: string[], leIds: string[] | null = null): boolean => {
  const { currentUserAuthorizations } = useAppSelector((state) => state.currentUserReducer);

  const isAuthorizedLeResult = useMemo(
    () => isAuthorizedLE(requiredAccessDomains, currentUserAuthorizations, leIds),
    [requiredAccessDomains, currentUserAuthorizations, leIds]
  );

  const canViewOrChangeClientDataResult = useMemo(
    () => currentUserAuthorizations?.canViewAll ?? false,
    [currentUserAuthorizations]
  );

  const attemptingSystemConfigAccessResult = useMemo(
    () => attemptingSystemConfigAccess(requiredAccessDomains, currentUserAuthorizations),
    [requiredAccessDomains, currentUserAuthorizations]
  );

  let result = false;

  if (!!currentUserAuthorizations) {
    result = isAuthorizedLeResult || (!attemptingSystemConfigAccessResult && canViewOrChangeClientDataResult);
  }

  return result;
};

const useEeGroupAuthorization = (requiredAccessDomains: string[], eeGroupIds: string[] | null = null): boolean => {
  const { currentUserAuthorizations } = useAppSelector((state) => state.currentUserReducer);

  const isAuthorizedResult = useMemo(
    () => isAuthorized(requiredAccessDomains, currentUserAuthorizations),
    [requiredAccessDomains, currentUserAuthorizations]
  );

  const isAuthorizedEeGroupResult = useMemo(
    () => isAuthorizedEeGroup(requiredAccessDomains, currentUserAuthorizations, eeGroupIds),
    [requiredAccessDomains, currentUserAuthorizations, eeGroupIds]
  );

  const canViewOrChangeClientDataResult = useMemo(
    () => currentUserAuthorizations?.canViewAll ?? false,
    [currentUserAuthorizations]
  );

  const attemptingSystemConfigAccessResult = useMemo(
    () => attemptingSystemConfigAccess(requiredAccessDomains, currentUserAuthorizations),
    [requiredAccessDomains, currentUserAuthorizations]
  );

  let result = false;

  if (!!currentUserAuthorizations) {
    result =
      isAuthorizedResult ||
      isAuthorizedEeGroupResult ||
      (!attemptingSystemConfigAccessResult && canViewOrChangeClientDataResult);
  }

  return result;
};

export default useAuthorization;

interface WidgetLinksProp {
  id: string;
  route: string;
}
